import type { CheckInPayload, KioskConfig } from '@foyer/shared';
import * as outbox from './outbox.js';

/**
 * API client for the tablet.
 *
 * Design rules that follow from "the tablet stands in a public hallway":
 *  - the device token lives in localStorage (it must survive a reboot) but is
 *    the *only* thing stored there; no visitor data ever is,
 *  - every submission goes through the outbox first, so the UI can confirm to
 *    the visitor as soon as the data is safely on the device,
 *  - the cached configuration lets the kiosk keep working through a backend
 *    outage instead of showing an error screen to a customer.
 */

const TOKEN_KEY = 'foyer.deviceToken';
const CONFIG_KEY = 'foyer.config';
const CONFIG_VERSION_KEY = 'foyer.configVersion';

export const APP_VERSION = '1.0.0';

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: Array<{ field: string; code?: string; message: string }>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Permanent errors must not be retried from the outbox. */
  get permanent(): boolean {
    return this.status >= 400 && this.status < 500 && this.code !== 'rate_limited';
  }
}

export function getDeviceToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setDeviceToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage disabled - the tablet then re-pairs after a reload */
  }
}

export function getCachedConfig(): KioskConfig | null {
  try {
    const raw = localStorage.getItem(CONFIG_KEY);
    return raw ? (JSON.parse(raw) as KioskConfig) : null;
  } catch {
    return null;
  }
}

function cacheConfig(config: KioskConfig): void {
  try {
    localStorage.setItem(CONFIG_KEY, JSON.stringify(config));
    localStorage.setItem(CONFIG_VERSION_KEY, config.configVersion);
  } catch {
    /* ignore quota errors - the kiosk still works, just without the cache */
  }
}

const BASE = '/api/v1';

interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  timeoutMs?: number;
  auth?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), options.timeoutMs ?? 15_000);

  const headers: Record<string, string> = { accept: 'application/json', ...options.headers };
  if (options.body !== undefined) headers['content-type'] = 'application/json';

  if (options.auth !== false) {
    const token = getDeviceToken();
    if (!token) throw new ApiError('device_not_paired', 'This tablet is not paired', 401);
    headers['x-device-token'] = token;
  }

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: controller.signal,
      cache: 'no-store',
    });
  } catch (err) {
    throw new ApiError('network_error', (err as Error).name === 'AbortError' ? 'Timeout' : 'Offline', 0);
  } finally {
    clearTimeout(timeout);
  }

  if (response.status === 304) return null as T;

  const text = await response.text();
  const data = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const body = data as {
      error?: {
        code?: string;
        message?: string;
        details?: Array<{ field: string; code?: string; message: string }>;
      };
    };
    throw new ApiError(
      body?.error?.code ?? 'internal_error',
      body?.error?.message ?? `Request failed (${response.status})`,
      response.status,
      body?.error?.details,
    );
  }

  return data as T;
}

/* -------------------------------------------------------------------- setup */

export interface SetupStatus {
  setupRequired: boolean;
  version: string;
}

/**
 * Whether the installation has been set up at all. A tablet cannot be paired
 * before that has happened, so an unpaired kiosk asks once and points the
 * operator at the admin wizard instead of showing a code field that no code
 * can satisfy yet.
 */
export function getSetupStatus(): Promise<SetupStatus> {
  return request<SetupStatus>('/setup/status', { auth: false, timeoutMs: 8000 });
}

/* ------------------------------------------------------------------ pairing */

export interface PairResult {
  deviceToken: string;
  config: KioskConfig;
}

export async function pair(pairingCode: string): Promise<KioskConfig> {
  const result = await request<PairResult>('/device/pair', {
    method: 'POST',
    auth: false,
    body: {
      pairingCode,
      appVersion: APP_VERSION,
      userAgent: navigator.userAgent.slice(0, 400),
      screen: `${window.screen.width}x${window.screen.height}`,
      os: navigator.platform || 'unknown',
    },
  });

  setDeviceToken(result.deviceToken);
  cacheConfig(result.config);
  return result.config;
}

/**
 * Drops everything that ties this browser to an installation.
 *
 * The outbox goes too. A revoked or unpaired tablet can never deliver what it
 * still holds - `flushOutbox` refuses without a token - so those entries would
 * sit on a device nobody trusts any more until the retention sweep eventually
 * reached them. Personal data on a decommissioned tablet is exactly what the
 * retention rules are about, so it is deleted the moment the pairing ends.
 */
export function unpair(): void {
  setDeviceToken(null);
  try {
    localStorage.removeItem(CONFIG_KEY);
    localStorage.removeItem(CONFIG_VERSION_KEY);
  } catch {
    /* ignore */
  }
  void outbox.clear().catch(() => undefined);
}

/* ------------------------------------------------------------------- config */

/**
 * Fetches the configuration, using the cached ETag so an unchanged config costs
 * a 304 instead of a full payload. Returns the cached copy when offline.
 */
export async function fetchConfig(): Promise<{ config: KioskConfig; fromCache: boolean }> {
  const cached = getCachedConfig();
  const headers: Record<string, string> = {};
  if (cached) headers['if-none-match'] = `"${cached.configVersion}"`;

  try {
    const fresh = await request<KioskConfig | null>('/device/config', { headers });
    if (fresh === null && cached) return { config: cached, fromCache: false };
    if (fresh) {
      cacheConfig(fresh);
      return { config: fresh, fromCache: false };
    }
  } catch (err) {
    if (err instanceof ApiError && err.permanent) throw err;
    if (cached) return { config: cached, fromCache: true };
    throw err;
  }

  if (cached) return { config: cached, fromCache: true };
  throw new ApiError('service_unavailable', 'Configuration not available', 503);
}

/* ---------------------------------------------------------------- heartbeat */

export interface HeartbeatResult {
  ok: boolean;
  serverTime: string;
  configVersion: string;
  open: boolean;
  openingReason: string;
}

export async function heartbeat(queued: number): Promise<HeartbeatResult> {
  return request<HeartbeatResult>('/device/heartbeat', {
    method: 'POST',
    timeoutMs: 8_000,
    body: {
      appVersion: APP_VERSION,
      userAgent: navigator.userAgent.slice(0, 400),
      screen: `${window.screen.width}x${window.screen.height}`,
      os: navigator.platform || 'unknown',
      queuedSubmissions: queued,
    },
  });
}

/* ----------------------------------------------------------------- check-in */

export interface SubmitResult {
  /** `queued` means: stored on the device, will be delivered automatically. */
  outcome: 'sent' | 'queued';
  ticketCode: string | null;
}

/**
 * Submits a check-in.
 *
 * The entry is written to the outbox first. If the request then succeeds the
 * entry is removed immediately; if it fails for any transient reason the visitor
 * still gets a confirmation because the data is safe and the flush loop will
 * deliver it. Only a permanent rejection (invalid data) surfaces as an error.
 */
export async function submitCheckIn(payload: CheckInPayload): Promise<SubmitResult> {
  await outbox.enqueue(payload).catch(() => {
    /* IndexedDB unavailable - fall through to a direct send */
  });

  try {
    const result = await request<{ visitorId: string; ticketCode: string | null; duplicate: boolean }>(
      '/device/checkins',
      { method: 'POST', body: payload, timeoutMs: 12_000 },
    );
    await outbox.remove(payload.idempotencyKey).catch(() => {});
    return { outcome: 'sent', ticketCode: result.ticketCode };
  } catch (err) {
    if (err instanceof ApiError && err.permanent) {
      await outbox.remove(payload.idempotencyKey).catch(() => {});
      throw err;
    }
    await outbox.markFailed(payload.idempotencyKey, String(err)).catch(() => {});
    return { outcome: 'queued', ticketCode: null };
  }
}

/**
 * Sends everything the outbox still holds. Called on reconnect, on a timer and
 * right after the app starts (which covers the tablet-reboot case).
 */
export async function flushOutbox(): Promise<{ sent: number; remaining: number }> {
  const entries = await outbox.list().catch(() => []);
  if (entries.length === 0) return { sent: 0, remaining: 0 };
  if (!getDeviceToken()) return { sent: 0, remaining: entries.length };

  let sent = 0;

  // Batched, so a tablet that was offline for an hour needs one request.
  const batches: (typeof entries)[] = [];
  for (let i = 0; i < entries.length; i += 25) batches.push(entries.slice(i, i + 25));

  for (const batch of batches) {
    try {
      const result = await request<{
        results: Array<{ idempotencyKey: string; ok: boolean; error?: string }>;
      }>('/device/checkins/batch', {
        method: 'POST',
        timeoutMs: 30_000,
        body: { submissions: batch.map((e) => e.payload) },
      });

      for (const r of result.results) {
        if (r.ok) {
          await outbox.remove(r.idempotencyKey).catch(() => {});
          sent++;
        } else if (r.error && r.error !== 'retry_later') {
          // The server will never accept this entry - drop it rather than
          // retrying forever, and leave a trace in the console for support.
          console.warn('[foyer] dropping rejected submission', r.idempotencyKey, r.error);
          await outbox.remove(r.idempotencyKey).catch(() => {});
        }
      }
    } catch {
      break; // still offline; try again on the next tick
    }
  }

  const remaining = await outbox.count();
  return { sent, remaining };
}

export { outbox };
